import { useState, useEffect } from 'react';
import axios from 'axios';

// Stato e handler dei filtri di ricerca documenti (testo libero, città, numero sentenza),
// duplicati in modo identico tra HomePage e UserDashboard prima di questa estrazione.
export function useDocumentFilters(token?: string | null) {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [numeroSentenza, setNumeroSentenza] = useState<string>('');
  const [numeroSentenzaInput, setNumeroSentenzaInput] = useState<string>('');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Carica le città disponibili al montaggio del componente (endpoint pubblico)
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await axios.get('/api/documents/cities', {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        setAvailableCities(response.data.cities || []);
      } catch (error) {
        console.error('Errore nel recupero delle città:', error);
      }
    };
    fetchCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleNumeroSentenzaInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNumeroSentenzaInput(event.target.value);
  };

  // Applica testo libero e numero sentenza in un'unica chiamata (le città si applicano subito
  // alla selezione, senza passare da qui). Chi la usa decide se chiamarla direttamente o
  // avvolgerla in un debounce (es. UserDashboard con useOptimizedSearch).
  const applyFilters = () => {
    setSearchTerm(inputValue);
    setNumeroSentenza(numeroSentenzaInput);
    setCurrentPage(1);
  };

  const handleCitiesChange = (newValue: string[]) => {
    setSelectedCities(newValue);
    setCurrentPage(1);
  };

  const removeCity = (city: string) => {
    setSelectedCities(prev => prev.filter(c => c !== city));
    setCurrentPage(1);
  };

  const clearSearchTerm = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const clearNumeroSentenza = () => {
    setNumeroSentenzaInput('');
    setNumeroSentenza('');
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedCities([]);
    setNumeroSentenzaInput('');
    setNumeroSentenza('');
    setCurrentPage(1);
  };

  const hasActiveFilters = Boolean(searchTerm || selectedCities.length > 0 || numeroSentenza);

  return {
    // Stato
    searchTerm,
    inputValue,
    selectedCities,
    availableCities,
    numeroSentenza,
    numeroSentenzaInput,
    showFilters,
    currentPage,
    hasActiveFilters,

    // Setter grezzi (utili per casi particolari, es. il ripristino dello stato di ricerca
    // salvato in location.state/sessionStorage nella UserDashboard)
    setSearchTerm,
    setInputValue,
    setSelectedCities,
    setNumeroSentenza,
    setNumeroSentenzaInput,
    setShowFilters,
    setCurrentPage,

    // Handler pronti per l'uso diretto nella UI
    handleInputChange,
    handleNumeroSentenzaInputChange,
    applyFilters,
    handleCitiesChange,
    removeCity,
    clearSearchTerm,
    clearNumeroSentenza,
    clearAllFilters,
  };
}

export default useDocumentFilters;
